import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { MessageSquare, Send } from 'lucide-react';

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
`;

const SettingsPanel = styled.div`
  width: 50%;
  padding: 24px;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PreviewPanel = styled.div`
  width: 50%;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  background-image: radial-gradient(#e5e7eb 1px, transparent 1px);
  background-size: 20px 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  width: 100%;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  width: 100%;
  background: #ffffff;
`;

const ChatWidget = styled(motion.div)`
  width: 320px;
  height: 500px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
`;

const WidgetHeader = styled.div`
  background: linear-gradient(135deg, #10b981, #059669);
  padding: 20px;
  color: #ffffff;
  text-align: center;
  position: relative;
`;

const BotAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #ffffff;
  margin: 0 auto 12px;
  padding: 2px;
  
  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const WidgetBody = styled.div`
  flex: 1;
  background: #f9fafb;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
`;

const ChatBubble = styled(motion.div) <{ isUser?: boolean }>`
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
  max-width: 85%;
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  background: ${props => props.isUser ? '#1f2937' : '#ffffff'};
  color: ${props => props.isUser ? '#ffffff' : '#1f2937'};
  border: ${props => props.isUser ? 'none' : '1px solid #e5e7eb'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const QuickReply = styled.div`
  background: #1f2937;
  color: #ffffff;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const ChatInput = styled.div`
  padding: 16px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 8px;
  
  input {
    flex: 1;
    border: none;
    font-size: 13px;
    outline: none;
    color: #4b5563;
    
    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const ChatWidgetVisual: React.FC = () => {
  return (
    <Container>
      <SettingsPanel>
        <FormGroup>
          <Label>Assistant Name</Label>
          <Input defaultValue="فهمة" />
        </FormGroup>
        <FormGroup>
          <Label>Message Prompt</Label>
          <Input defaultValue="Type your query or hit '/' for menu" />
        </FormGroup>
        <FormGroup>
          <Label>Intro Message Text</Label>
          <TextArea defaultValue="مرحباً بك في WLCD Academy 👋&#10;اسألني عن الدورات، الدعم، أو أي شيء يخص الموقع أنا هنا لمساعدتك" />
        </FormGroup>
        <FormGroup>
          <Label>Default Language</Label>
          <Select defaultValue="English">
            <option>English</option>
            <option>Arabic</option>
            <option>Spanish</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Bot Tagline</Label>
          <Input defaultValue="مساعدك الأكاديمي بالذكاء الاصطناعي" />
        </FormGroup>
      </SettingsPanel>

      <PreviewPanel>
        <ChatWidget
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <WidgetHeader>
            <BotAvatar>
              <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Felix" alt="Bot" />
            </BotAvatar>
            <div style={{ fontWeight: 600, fontSize: 16 }}>فهمة</div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>مساعدك الأكاديمي بالذكاء الاصطناعي</div>
          </WidgetHeader>
          <WidgetBody>
            <ChatBubble
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ background: '#1f2937', color: '#ffffff' }}
            >
              مرحباً بك في WLCD Academy 👋<br />
              اسألني عن الدورات، الدعم، أو أي شيء يخص الموقع أنا هنا لمساعدتك
            </ChatBubble>

            <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280', textAlign: 'center', marginBottom: 8 }}>
              Type or choose from the options below
            </div>

            <QuickReply>
              كيف يمكنني إنشاء حساب؟
              <MessageSquare size={12} />
            </QuickReply>
            <QuickReply>
              هل توفرون دعماً تقنياً؟
              <MessageSquare size={12} />
            </QuickReply>
            <QuickReply>
              هل أحصل على شهادة بعد إنهاء الدورة؟
              <MessageSquare size={12} />
            </QuickReply>
          </WidgetBody>
          <ChatInput>
            <MessageSquare size={16} color="#9ca3af" />
            <input placeholder="Type your query or hit '/' for menu" />
            <Send size={16} color="#10b981" />
          </ChatInput>
        </ChatWidget>
      </PreviewPanel>
    </Container>
  );
};

export default ChatWidgetVisual;
